import { useState } from 'react';

interface HeatmapChartProps {
  data: { day: number; weekday: number; value: number }[];
  maxWeeks?: number;
  color?: string;
}

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function HeatmapChart({ data, maxWeeks = 5, color = '#0d9488' }: HeatmapChartProps) {
  const [hovered, setHovered] = useState<{ day: number; value: number } | null>(null);

  if (data.length === 0) return null;

  const maxVal = Math.max(...data.map(d => d.value), 1);
  const cellSize = 28;
  const gap = 3;
  const labelWidth = 32;

  const weeks: Record<number, { day: number; weekday: number; value: number }[]> = {};
  for (const d of data) {
    const weekIdx = Math.floor((d.day - 1) / 7);
    if (!weeks[weekIdx]) weeks[weekIdx] = [];
    weeks[weekIdx].push(d);
  }

  const numWeeks = Math.min(Object.keys(weeks).length, maxWeeks);
  const svgWidth = labelWidth + numWeeks * (cellSize + gap) + 40;
  const svgHeight = 7 * (cellSize + gap) + 20;

  function getOpacity(value: number) {
    if (value === 0) return 0.06;
    return 0.15 + (value / maxVal) * 0.85;
  }

  return (
    <div className="w-full overflow-x-auto">
      <svg width={svgWidth} height={svgHeight} viewBox={`0 0 ${svgWidth} ${svgHeight}`}>
        {WEEKDAY_LABELS.map((label, i) => (
          <text key={label} x={labelWidth - 6} y={i * (cellSize + gap) + cellSize / 2 + 4} textAnchor="end" fill="rgba(255,255,255,0.7)" fontSize="10">
            {label}
          </text>
        ))}

        {data.map((d) => {
          const weekIdx = Math.floor((d.day - 1) / 7);
          const x = labelWidth + weekIdx * (cellSize + gap);
          const y = d.weekday * (cellSize + gap);
          const isHov = hovered?.day === d.day;

          return (
            <g
              key={d.day}
              onMouseEnter={() => setHovered(d)}
              onMouseLeave={() => setHovered(null)}
              style={{ cursor: 'pointer' }}
            >
              <rect
                x={x}
                y={y}
                width={cellSize}
                height={cellSize}
                rx={4}
                fill={color}
                opacity={getOpacity(d.value)}
                stroke={isHov ? color : 'transparent'}
                strokeWidth={isHov ? 2 : 0}
                style={{ transition: 'all 0.15s' }}
              />
              {d.value > 0 && (
                <text x={x + cellSize / 2} y={y + cellSize / 2 + 4} textAnchor="middle" fill={getOpacity(d.value) > 0.5 ? '#fff' : 'rgba(255,255,255,0.7)'} fontSize="9" fontWeight="500">
                  {d.value}
                </text>
              )}
              {isHov && (
                <>
                  <rect x={x + cellSize / 2 - 32} y={y - 20} width={64} height={16} rx={3} fill="#1e293b" opacity="0.92" />
                  <text x={x + cellSize / 2} y={y - 8} textAnchor="middle" fill="#fff" fontSize="9" fontWeight="600">
                    Day {d.day}: {d.value}
                  </text>
                </>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
